import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * EmailPage — P1-EM05-lite (Wave A) admin surface for outbound email.
 *
 * Two panes — minimal MVP. Wave B (EM05-full) adds a third pane for the
 * per-app SMTP credential viewer + a fourth pane for the per-app
 * from-address override.
 *
 *   ┌────────────── Send test email ───────────────────────────────┐
 *   │  To  [_________________]   Subject  [____________________]  │
 *   │  Body                                                       │
 *   │  [_______________________________________________________]  │
 *   │  [ Send ]                            Result panel ──────►   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 *   ┌────────────── Recent sends ──────────────────────────────────┐
 *   │  filters: status · kind   refresh                            │
 *   │  time · to · subject · kind · status · message_id            │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Talks exclusively to:
 *   POST /v1/admin/email/test     → send + log + audit
 *   GET  /v1/admin/email/sends    → paginated history
 *
 * Validates end-to-end that the Gmail SMTP relay is wired correctly
 * through the production-shaped path (operator → POST /test → singleton
 * Mailer → nodemailer → Gmail → recipient). When Wave B lands, the
 * underlying mailer swap is invisible to this page.
 */
import { useCallback, useEffect, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
import { CreateCredentialModal, emptyCredentialDraft, draftToPatchBody, } from './credentials/CreateCredentialModal.js';
function emptyDraft() {
    return {
        to: '',
        from: '',
        subject: '',
        body: '',
    };
}
export function EmailPage() {
    const fetch = useNexusFetch();
    // Send test panel state
    const [draft, setDraft] = useState(emptyDraft);
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    // History panel state
    const [sends, setSends] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [kindFilter, setKindFilter] = useState('all');
    const [topBanner, setTopBanner] = useState(null);
    // SMTP credential pane state
    const [smtpCreds, setSmtpCreds] = useState([]);
    const [loadingCreds, setLoadingCreds] = useState(false);
    const [credEditForm, setCredEditForm] = useState(null);
    // ── Load history ────────────────────────────────────────────────
    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        setTopBanner(null);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all')
                params.set('status', statusFilter);
            if (kindFilter !== 'all')
                params.set('kind', kindFilter);
            params.set('limit', '50');
            const qs = params.toString();
            const resp = await fetch(`/v1/admin/email/sends?${qs}`);
            setSends(resp.sends ?? []);
        }
        catch (e) {
            setTopBanner(formatError(e));
        }
        finally {
            setLoadingHistory(false);
        }
    }, [fetch, statusFilter, kindFilter]);
    useEffect(() => {
        void loadHistory();
    }, [loadHistory]);
    // ── Load SMTP credentials ───────────────────────────────────────
    const loadSmtpCreds = useCallback(async () => {
        setLoadingCreds(true);
        try {
            const resp = await fetch('/v1/admin/credentials?type=smtp');
            setSmtpCreds(resp.credentials ?? []);
        }
        catch (e) {
            // Non-fatal — the pane just shows an empty state. Top banner
            // would crowd the test-send panel, which is the user's main goal.
            console.warn('EmailPage: smtp credential load failed', e);
        }
        finally {
            setLoadingCreds(false);
        }
    }, [fetch]);
    useEffect(() => {
        void loadSmtpCreds();
    }, [loadSmtpCreds]);
    // ── Edit SMTP credential (opens modal) ──────────────────────────
    const openEditCredential = useCallback((row) => {
        const ec = (row.extraConfig ?? {});
        setCredEditForm({
            original: row,
            draft: {
                ...emptyCredentialDraft(),
                name: row.name,
                credentialType: row.credentialType,
                identityRef: row.identityRef ?? '',
                smtpUsername: typeof ec.username === 'string' ? ec.username : '',
                smtpPassword: '',
                smtpHost: typeof ec.host === 'string' ? ec.host : '',
                smtpPort: typeof ec.port === 'number' || typeof ec.port === 'string'
                    ? String(ec.port)
                    : '587',
                smtpSecure: ec.secure === true || ec.secure === 'true',
                smtpFrom: typeof ec.from === 'string' ? ec.from : '',
            },
            generalErrors: [],
            formBanner: null,
            submitting: false,
        });
    }, []);
    const closeCredEdit = useCallback(() => setCredEditForm(null), []);
    const updateCredDraft = useCallback((key, value) => {
        setCredEditForm((prev) => prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev);
    }, []);
    const onSubmitCredEdit = useCallback(async (e) => {
        e.preventDefault();
        if (!credEditForm)
            return;
        setCredEditForm({ ...credEditForm, submitting: true, formBanner: null });
        const body = draftToPatchBody(credEditForm.draft);
        if (!body.ok) {
            setCredEditForm({
                ...credEditForm,
                formBanner: body.error,
                submitting: false,
            });
            return;
        }
        try {
            await fetch(`/v1/admin/credentials/${credEditForm.original.id}`, {
                method: 'PATCH',
                json: body.body,
            });
            setCredEditForm(null);
            void loadSmtpCreds();
        }
        catch (e) {
            setCredEditForm({
                ...credEditForm,
                formBanner: formatError(e),
                submitting: false,
            });
        }
    }, [credEditForm, fetch, loadSmtpCreds]);
    // ── Send test ───────────────────────────────────────────────────
    const onSubmitTest = useCallback(async (e) => {
        e.preventDefault();
        const to = draft.to.trim();
        if (!to) {
            setLastResult({
                at: new Date().toISOString(),
                status: 'error',
                message: 'Recipient address is required.',
            });
            return;
        }
        setSending(true);
        try {
            const body = { to };
            if (draft.from.trim())
                body.from = draft.from.trim();
            if (draft.subject.trim())
                body.subject = draft.subject.trim();
            if (draft.body.length > 0)
                body.body = draft.body;
            const resp = await fetch('/v1/admin/email/test', {
                method: 'POST',
                body: JSON.stringify(body),
            });
            setLastResult({
                at: new Date().toISOString(),
                status: resp.status,
                message: resp.status === 'sent'
                    ? `Delivered to ${to} in ${resp.latencyMs} ms.`
                    : resp.errorMessage ?? 'Send failed (no error returned).',
                latencyMs: resp.latencyMs,
                messageId: resp.messageId,
            });
            // Refresh history so the new row shows up immediately.
            void loadHistory();
        }
        catch (e) {
            setLastResult({
                at: new Date().toISOString(),
                status: 'error',
                message: formatError(e),
            });
        }
        finally {
            setSending(false);
        }
    }, [fetch, draft, loadHistory]);
    // ── Render ──────────────────────────────────────────────────────
    return (_jsxs("div", { className: "container-fluid p-4", children: [_jsx("div", { className: "d-flex align-items-center justify-content-between mb-3", children: _jsxs("div", { children: [_jsx("h2", { className: "mb-1", children: "Email" }), _jsxs("div", { className: "text-muted small", children: ["Send a test email to verify the SMTP relay is wired correctly, and audit recent outbound mail. Wave A: uses the singleton SMTP transport built at boot from ", _jsx("code", { children: "instance.env" }), ". Per-app credential viewer + from-address override land in Wave B."] })] }) }), topBanner ? (_jsx("div", { className: "alert alert-warning", role: "alert", children: topBanner })) : null, _jsxs("div", { className: "card mb-4", children: [_jsx("div", { className: "card-header", children: _jsx("strong", { children: "Send test email" }) }), _jsx("div", { className: "card-body", children: _jsx("form", { onSubmit: onSubmitTest, children: _jsxs("div", { className: "row g-3", children: [_jsxs("div", { className: "col-12", children: [_jsxs("label", { htmlFor: "email-from", className: "form-label small mb-1", children: ["From", ' ', _jsxs("span", { className: "text-muted", children: ["(optional override; e.g.", ' ', _jsx("code", { children: "noreply@aquastarpoolproducts.com" }), " or", ' ', _jsx("code", { children: "AquaStar Support <noreply@aquastarpoolproducts.com>" }), ")"] })] }), _jsx("input", { id: "email-from", type: "text", className: "form-control", placeholder: "leave blank to use the mailer default", value: draft.from, onChange: (e) => setDraft((d) => ({ ...d, from: e.target.value })), disabled: sending }), _jsxs("div", { className: "form-text small", children: ["Gmail SMTP only honours From addresses that match the authenticated account or a verified ", _jsx("em", { children: "Send mail as" }), " alias. Other senders are typically rewritten or rejected. SES / Postmark / SendGrid have their own per-provider rules."] })] }), _jsxs("div", { className: "col-md-6", children: [_jsx("label", { htmlFor: "email-to", className: "form-label small mb-1", children: "To" }), _jsx("input", { id: "email-to", type: "email", className: "form-control", placeholder: "someone@example.com", value: draft.to, onChange: (e) => setDraft((d) => ({ ...d, to: e.target.value })), disabled: sending, required: true })] }), _jsxs("div", { className: "col-md-6", children: [_jsxs("label", { htmlFor: "email-subject", className: "form-label small mb-1", children: ["Subject", ' ', _jsx("span", { className: "text-muted", children: "(optional \u2014 server fills in a timestamp default)" })] }), _jsx("input", { id: "email-subject", type: "text", className: "form-control", placeholder: "Nexus test email", value: draft.subject, onChange: (e) => setDraft((d) => ({ ...d, subject: e.target.value })), disabled: sending })] }), _jsxs("div", { className: "col-12", children: [_jsxs("label", { htmlFor: "email-body", className: "form-label small mb-1", children: ["Body", ' ', _jsx("span", { className: "text-muted", children: "(optional \u2014 server fills in default copy)" })] }), _jsx("textarea", { id: "email-body", className: "form-control", rows: 3, placeholder: "If you received this, the Nexus admin email test path is working end-to-end.", value: draft.body, onChange: (e) => setDraft((d) => ({ ...d, body: e.target.value })), disabled: sending })] }), _jsxs("div", { className: "col-12 d-flex align-items-center gap-3", children: [_jsx("button", { type: "submit", className: "btn btn-primary", disabled: sending || !draft.to.trim(), children: sending ? 'Sending…' : 'Send test email' }), lastResult ? (_jsx(ResultPanel, { result: lastResult })) : (_jsx("span", { className: "text-muted small", children: "No test sends yet this session." }))] })] }) }) })] }), _jsxs("div", { className: "card mb-4", children: [_jsxs("div", { className: "card-header d-flex align-items-center justify-content-between", children: [_jsx("strong", { children: "SMTP credential" }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => void loadSmtpCreds(), disabled: loadingCreds, children: loadingCreds ? 'Loading…' : 'Refresh' })] }), _jsxs("div", { className: "card-body", children: [smtpCreds.length === 0 && !loadingCreds ? (_jsxs("div", { className: "text-muted small", children: ["No SMTP credential rows in ", _jsx("code", { children: "nexus_credentials" }), " yet. The mailer is reading SMTP config from", ' ', _jsx("code", { children: "instance.env" }), " (legacy env fallback). Seed one via", ' ', _jsx("code", { children: "nexus admin upsert-credential" }), " in the deploy script, OR click ", _jsx("strong", { children: "+ Add credential" }), " on the Credentials page with type ", _jsx("code", { children: "smtp" }), "."] })) : (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm align-middle mb-0", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Host : Port" }), _jsx("th", { children: "TLS" }), _jsx("th", { children: "From" }), _jsx("th", { children: "Fingerprint" }), _jsx("th", { children: "Owner" }), _jsx("th", { children: "Updated" }), _jsx("th", { className: "text-end", children: "Actions" })] }) }), _jsx("tbody", { children: smtpCreds.map((row) => {
                                                const ec = (row.extraConfig ?? {});
                                                const host = typeof ec.host === 'string' ? ec.host : '—';
                                                const port = typeof ec.port === 'number' || typeof ec.port === 'string'
                                                    ? String(ec.port)
                                                    : '—';
                                                const secure = ec.secure === true || ec.secure === 'true'
                                                    ? 'implicit'
                                                    : 'STARTTLS';
                                                const from = typeof ec.from === 'string' ? ec.from : '—';
                                                return (_jsxs("tr", { children: [_jsx("td", { className: "font-monospace small", children: row.name }), _jsxs("td", { className: "font-monospace small", children: [host, ":", port] }), _jsx("td", { className: "small", children: secure }), _jsx("td", { className: "small text-truncate", style: { maxWidth: 280 }, children: from }), _jsx("td", { className: "font-monospace small text-danger", children: row.fingerprint }), _jsx("td", { children: row.deployScriptManaged ? (_jsx("span", { className: "badge bg-info text-dark", title: "Seeded by deploy script. Next re-deploy may refresh it.", children: "deploy" })) : (_jsx("span", { className: "badge bg-success", title: "Edited via admin UI. Deploy script will skip this row.", children: "ui" })) }), _jsx("td", { className: "small text-muted", children: formatRelativeTime(row.updatedAt) }), _jsx("td", { className: "text-end", children: _jsx("button", { type: "button", className: "btn btn-outline-primary btn-sm", onClick: () => openEditCredential(row), children: "Edit" }) })] }, row.id));
                                            }) })] }) })), _jsxs("div", { className: "form-text small mt-2", children: ["Editing flips ownership to ", _jsx("strong", { children: "ui" }), " \u2014 the next deploy will skip the row, so your changes survive. The mailer picks the change up on the next outbound email \u2014 no restart (NEXUS-77)."] })] })] }), _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex align-items-center justify-content-between", children: [_jsx("strong", { children: "Recent sends" }), _jsxs("div", { className: "d-flex gap-2 align-items-center", children: [_jsxs("select", { className: "form-select form-select-sm", style: { width: 'auto' }, value: kindFilter, onChange: (e) => setKindFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All kinds" }), _jsx("option", { value: "test", children: "test" }), _jsx("option", { value: "otp", children: "otp" }), _jsx("option", { value: "notification", children: "notification" }), _jsx("option", { value: "other", children: "other" })] }), _jsxs("select", { className: "form-select form-select-sm", style: { width: 'auto' }, value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All statuses" }), _jsx("option", { value: "sent", children: "sent" }), _jsx("option", { value: "failed", children: "failed" })] }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => void loadHistory(), disabled: loadingHistory, children: loadingHistory ? 'Loading…' : 'Refresh' })] })] }), _jsx("div", { className: "card-body p-0", children: _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm table-hover mb-0", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { children: "Time" }), _jsx("th", { children: "From" }), _jsx("th", { children: "To" }), _jsx("th", { children: "Subject" }), _jsx("th", { children: "Kind" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "App" }), _jsx("th", { children: "Message ID" })] }) }), _jsx("tbody", { children: sends.length === 0 && !loadingHistory ? (_jsx("tr", { children: _jsx("td", { colSpan: 8, className: "text-muted text-center py-4", children: "No sends yet." }) })) : (sends.map((row) => (_jsxs("tr", { children: [_jsx("td", { className: "text-nowrap small", children: formatRelativeTime(row.sentAt) }), _jsx("td", { className: "text-nowrap small", children: row.fromAddress ?? (_jsx("span", { className: "text-muted", children: "default" })) }), _jsx("td", { className: "text-nowrap", children: row.toAddress }), _jsx("td", { children: row.subject }), _jsx("td", { children: _jsx("span", { className: "badge bg-secondary", children: row.kind }) }), _jsx("td", { children: _jsx(StatusBadge, { status: row.status, error: row.errorMessage }) }), _jsx("td", { className: "small text-muted", children: row.appId }), _jsx("td", { className: "small text-muted text-truncate", style: { maxWidth: 200 }, children: row.messageId ?? '—' })] }, row.id)))) })] }) }) })] }), credEditForm && (_jsx(CreateCredentialModal, { mode: "edit", original: {
                    name: credEditForm.original.name,
                    credentialType: credEditForm.original.credentialType,
                    fingerprint: credEditForm.original.fingerprint,
                    deployScriptManaged: credEditForm.original.deployScriptManaged,
                }, draft: credEditForm.draft, fieldErrors: {}, generalErrors: credEditForm.generalErrors, formBanner: credEditForm.formBanner, submitting: credEditForm.submitting, onChange: updateCredDraft, onCancel: closeCredEdit, onSubmit: onSubmitCredEdit }))] }));
}
// ── Sub-components / helpers ───────────────────────────────────────
function ResultPanel({ result }) {
    const cls = result.status === 'sent'
        ? 'alert alert-success py-1 px-3 mb-0 small'
        : result.status === 'failed'
            ? 'alert alert-warning py-1 px-3 mb-0 small'
            : 'alert alert-danger py-1 px-3 mb-0 small';
    return (_jsxs("div", { className: cls, style: { flex: 1 }, children: [_jsx("strong", { className: "text-uppercase me-2", children: result.status }), result.message, result.messageId ? (_jsxs("span", { className: "ms-2 text-muted", children: ["(message-id: ", _jsx("code", { children: result.messageId }), ")"] })) : null] }));
}
function StatusBadge({ status, error, }) {
    if (status === 'sent') {
        return _jsx("span", { className: "badge bg-success", children: "sent" });
    }
    if (status === 'failed') {
        return (_jsx("span", { className: "badge bg-warning text-dark", title: error ?? 'unknown error', children: "failed" }));
    }
    return _jsx("span", { className: "badge bg-secondary", children: status });
}
function formatError(e) {
    if (e instanceof NexusAdminApiError) {
        return `${e.code}: ${e.message}`;
    }
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
function formatRelativeTime(iso) {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then))
        return iso;
    const delta = Date.now() - then;
    const sec = Math.round(delta / 1000);
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 7)
        return `${day}d ago`;
    return new Date(iso).toLocaleString();
}
//# sourceMappingURL=EmailPage.js.map